import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Typography,
} from "@material-ui/core";
import CustomButton from "../../../../components/CustomButton";
import icons from "../../../../assets/icons/icon-collection.svg";
import "./JobFilters.css";

// ─── Config ────────────────────────────────────────────────────────────────────

const FILTER_CONFIG = {
  jobTier: {
    title: "Job Tier",
    type: "radio",
    options: [
      { value: "", label: "All Tiers" },
      { value: "1", label: "Tier 1" },
      { value: "2", label: "Tier 2" },
      { value: "3", label: "Tier 3" },
    ],
  },
  datePosted: {
    title: "Date Posted",
    type: "radio",
    options: [
      { value: "", label: "Any Time" },
      { value: "today", label: "Today" },
      { value: "3days", label: "Last 3 Days" },
      { value: "7days", label: "Last 7 Days" },
      { value: "14days", label: "Last 14 Days" },
    ],
  },
  jobType: {
    title: "Job Type",
    type: "checkbox",
    options: [
      { value: "onsite", label: "On-site" },
      { value: "remote", label: "Remote" },
      { value: "hybrid", label: "Hybrid" },
    ],
  },
  experienceLevel: {
    title: "Experience Level",
    type: "checkbox-grouped",
    groups: [
      {
        label: "Entry Level",
        options: [
          { value: "graduate", label: "Graduate / Intern" },
          { value: "entry", label: "Entry Level (0–2 years)" },
        ],
      },
      {
        label: "Mid Level",
        options: [
          { value: "mid", label: "Mid Level (3–5 years)" },
          { value: "senior", label: "Senior (6–10 years)" },
        ],
      },
      {
        label: "Senior Level",
        options: [
          { value: "manager", label: "Manager / Lead" },
          { value: "executive", label: "Executive / Director" },
        ],
      },
    ],
  },
};

const CHECKBOX_FILTER_TYPES = ["jobType", "experienceLevel"];

// ─── Sub-components ────────────────────────────────────────────────────────────

const RadioFilter = ({ options, value, onChange }) => (
  <RadioGroup value={value} onChange={onChange}>
    {options.map(({ value: val, label }) => (
      <FormControlLabel
        key={val}
        value={val}
        control={<Radio color="primary" />}
        label={label}
      />
    ))}
  </RadioGroup>
);

const CheckboxFilter = ({ options, value, onChange }) => (
  <FormGroup>
    {options.map(({ value: val, label }) => (
      <FormControlLabel
        key={val}
        control={
          <Checkbox
            checked={Array.isArray(value) && value.includes(val)}
            onChange={onChange}
            value={val}
            color="primary"
          />
        }
        label={label}
      />
    ))}
  </FormGroup>
);

const CheckboxGroupedFilter = ({ groups, value, onChange }) => (
  <FormGroup>
    {groups.map(({ label, options }) => (
      <div key={label} className="experience-group">
        <div className="experience-group-title">{label}</div>
        {options.map(({ value: val, label: optLabel }) => (
          <FormControlLabel
            key={val}
            control={
              <Checkbox
                checked={Array.isArray(value) && value.includes(val)}
                onChange={onChange}
                value={val}
                color="primary"
              />
            }
            label={optLabel}
          />
        ))}
      </div>
    ))}
  </FormGroup>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const JobFilters = ({ filterType, open, onClose, onApplyFilter, currentValue }) => {
  const [selectedValue, setSelectedValue] = useState(currentValue);

  const config = FILTER_CONFIG[filterType] ?? null;
  const isCheckboxType = CHECKBOX_FILTER_TYPES.includes(filterType);

  useEffect(() => {
    setSelectedValue(currentValue);
  }, [filterType, currentValue, open]);

  const handleRadioChange = (e) => setSelectedValue(e.target.value);

  const handleCheckboxChange = (e) => {
    const val = e.target.value;
    const current = Array.isArray(selectedValue) ? selectedValue : [];
    setSelectedValue(
      current.includes(val) ? current.filter((v) => v !== val) : [...current, val]
    );
  };

  const handleClear = () => setSelectedValue(isCheckboxType ? [] : "");

  const handleApply = () => {
    onApplyFilter(filterType, selectedValue);
    onClose();
  };

  const renderContent = () => {
    if (!config) return null;
    if (config.type === "radio")
      return <RadioFilter options={config.options} value={selectedValue} onChange={handleRadioChange} />;
    if (config.type === "checkbox")
      return <CheckboxFilter options={config.options} value={selectedValue} onChange={handleCheckboxChange} />;
    if (config.type === "checkbox-grouped")
      return <CheckboxGroupedFilter groups={config.groups} value={selectedValue} onChange={handleCheckboxChange} />;
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="filter-dialog"
      PaperProps={{
        style: { width: "450px", maxWidth: "90vw", borderRadius: "12px", padding: "8px" },
      }}
    >
      <DialogTitle id="filter-dialog">
        <div className="d-flex justify-content-between align-items-center">
          <Typography variant="h6" className="mb-0 font-weight-bold">
            {config?.title ?? "Filter"}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <svg width="24" height="24">
              <use href={`${icons}#close`} />
            </svg>
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent
        dividers
        style={{ maxHeight: "60vh", overflowY: "auto", padding: "20px" }}
      >
        <FormControl component="fieldset" fullWidth>
          {renderContent()}
        </FormControl>
      </DialogContent>

      <DialogActions className="d-flex justify-content-between p-3">
        <CustomButton
          text="Clear"
          handleClick={handleClear}
          backgroundColor="#e3e3e3"
          color="#333333"
          additionalClass="px-4"
        />
        <div className="d-flex" style={{ gap: "12px" }}>
          <CustomButton
            text="Cancel"
            handleClick={onClose}
            backgroundColor="#6c757d"
            color="#fff"
            additionalClass="px-4"
          />
          <CustomButton
            text="Apply"
            handleClick={handleApply}
            backgroundColor="#3A6ED4"
            color="#fff"
            additionalClass="px-4"
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default JobFilters;